import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell
} from 'recharts';
import { Box, Grid, Typography, Paper, FormControl, Select, MenuItem } from '@mui/material';

const Dashboard = () => {
  const [view, setView] = useState('daily');
  const [selectedGraph, setSelectedGraph] = useState('all');
  const [dadosDiario, setDadosDiario] = useState([]);
  const [dadosMensais, setDadosMensais] = useState([]);

  useEffect(() => {
    fetch('https://sense-agro-backend.onrender.com/api/leituras-diarias')
      .then(res => res.json())
      .then(data => setDadosDiario(data));

    fetch('https://sense-agro-backend.onrender.com/api/leituras-mensais')
      .then(res => res.json())
      .then(data => setDadosMensais(data));
  }, []);

  const renderGraph = (type) => {
    switch (type) {
      case 'Temperatura Diária':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={view === 'daily' ? dadosDiario : dadosMensais}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={view === 'daily' ? 'date' : 'mes'} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey={view === 'daily' ? 'temperatura' : 'temperaturaMedia'}
                stroke="#6366f1"
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'Consumo de Água':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dadosMensais}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="temperaturaMedia" fill="#34d399" />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'Indicadores de Eficiência':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={dadosMensais}
                dataKey="temperaturaMedia"
                nameKey="mes"
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#6366f1"
              >
                {dadosMensais.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={['#6366f1', '#34d399', '#f59e0b'][index % 3]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );
      case 'Comparação por Áreas':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={dadosMensais}>
              <PolarGrid />
              <PolarAngleAxis dataKey="mes" />
              <PolarRadiusAxis />
              <Radar
                name="Indicadores"
                dataKey="temperaturaMedia"
                fill="#60a5fa"
                fillOpacity={0.6}
              />
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        );
      case 'all':
      default:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" align="center" gutterBottom>
                  Temperatura Diária
                </Typography>
                {renderGraph('Temperatura Diária')}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" align="center" gutterBottom>
                  Consumo de Água
                </Typography>
                {renderGraph('Consumo de Água')}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" align="center" gutterBottom>
                  Indicadores de Eficiência
                </Typography>
                {renderGraph('Indicadores de Eficiência')}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" align="center" gutterBottom>
                  Comparação por Áreas
                </Typography>
                {renderGraph('Comparação por Áreas')}
              </Paper>
            </Grid>
          </Grid>
        );
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', padding: 4 }}>
      <Typography variant="h4" align="left" gutterBottom color="#1f2937">
        Dashboard
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: 'Eficiência Geral', value: '85%' },
          { label: 'Consumo de Água', value: '72%' },
          { label: 'Produtividade', value: '92%' },
          { label: 'Saúde do Solo', value: '88%' },
        ].map((card, i) => (
          <Grid key={i} item xs={12} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                {card.label}
              </Typography>
              <Typography variant="h4" color="primary">
                {card.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item>
          <Typography variant="subtitle1">Seleção de Período</Typography>
          <FormControl fullWidth>
            <Select
              value={view}
              onChange={(e) => setView(e.target.value)}
              size="small"
            >
              <MenuItem value="daily">Diário</MenuItem>
              <MenuItem value="monthly">Mensal</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item>
          <Typography variant="subtitle1">Seleção de Gráficos</Typography>
          <FormControl fullWidth>
            <Select
              value={selectedGraph}
              onChange={(e) => setSelectedGraph(e.target.value)}
              size="small"
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="Temperatura Diária">Temperatura</MenuItem>
              <MenuItem value="Consumo de Água">Consumo</MenuItem>
              <MenuItem value="Indicadores de Eficiência">Eficiência</MenuItem>
              <MenuItem value="Comparação por Áreas">Radar</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {renderGraph(selectedGraph)}
    </Box>
  );
};

export default Dashboard;
